import { Button, Card, CardHeader, CardBody, Form, Badge, Input, Label, FormGroup, Collapse, FormText, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useEffect, useState } from 'react';
import './census-sync.styl';

export const OPERATION_LABELS = {
  update: "Update",
  upsert: "Upsert (Update or Insert)",
  insert: "Insert",
  mirror: "Mirror",
  append: "Append",
  delete: "Delete",
}

const OPERATION_DESCRIPTIONS = {
  update: "Update existing records only",
  upsert: "Update existing records or insert new ones",
  insert: "Insert new records only",
  mirror: "Mirror the source data exactly (add, update, delete)",
  append: "Append new records without checking for duplicates",
  delete: "Delete records from destination",
}

// Fields that should always be visible if present (Tier 3)
const ALWAYS_VISIBLE_FIELDS = [
  'http_endpoint',
  'file_format',
  'file_path',
  'distance_metric',
  'dimension',
];

// Fields that can use defaults silently (Tier 1)
const SILENT_DEFAULT_FIELDS = [
  'include_header',
  'quote_character',
  'rate_limit',
];

// Categorize advanced config fields into tiers
function categorizeFields(definitions, selectedOperation) {
  if (!definitions || definitions.length === 0) {
    return { alwaysVisible: [], advancedSettings: [], silentDefaults: [] };
  }

  const alwaysVisible = [];
  const advancedSettings = [];
  const silentDefaults = [];

  for (const def of definitions) {
    // Check for operation-specific conditional display
    if (def.name === 'truncate_before_load' && selectedOperation !== 'mirror') {
      silentDefaults.push(def);
      continue;
    }

    // Tier 3: Required or critical - always visible
    if (def.required || ALWAYS_VISIBLE_FIELDS.includes(def.name)) {
      alwaysVisible.push(def);
      continue;
    }

    // Tier 1: Can use defaults silently
    if (def.default !== undefined && SILENT_DEFAULT_FIELDS.includes(def.name)) {
      silentDefaults.push(def);
      continue;
    }

    // Tier 2: Show in advanced settings
    advancedSettings.push(def);
  }

  // Sort by sort_order if available
  const sortByOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0);
  alwaysVisible.sort(sortByOrder);
  advancedSettings.sort(sortByOrder);

  return { alwaysVisible, advancedSettings, silentDefaults };
}

// Build initial config with defaults
function buildDefaultConfig(definitions) {
  const config = {};
  for (const def of definitions || []) {
    if (def.default !== undefined) {
      config[def.name] = def.default;
    }
  }
  return config;
}

export default function SyncBehaviorSelector({
  destinationObject,
  hasMappingChanges,
  onSelect,
}) {
  const [objectDetails, setObjectDetails] = useState(destinationObject || null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [advancedConfig, setAdvancedConfig] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, pendingOperation: null });
  const supportedOperations = objectDetails?.supported_operations || [];
  const advancedFieldDefinitions = objectDetails?.advanced_configuration_field_definitions || [];

  // Reset selection when destination object changes and auto-select (prefer 'upsert' if available)
  useEffect(() => {
    setObjectDetails(destinationObject || null);
    const operations = destinationObject?.supported_operations || [];
    const definitions = destinationObject?.advanced_configuration_field_definitions || [];

    // Initialize advanced config with defaults
    const defaultConfig = buildDefaultConfig(definitions);
    setAdvancedConfig(defaultConfig);

    if (operations.length > 0) {
      // Prefer 'upsert' if available, otherwise use first option
      const defaultOperation = operations.includes('upsert') ? 'upsert' : operations[0];
      setSelectedOperation(defaultOperation);
      onSelect(defaultOperation, destinationObject, defaultConfig);
    } else {
      setSelectedOperation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationObject]);

  // Update config value
  const updateConfig = (fieldName, value) => {
    const newConfig = { ...advancedConfig, [fieldName]: value };
    setAdvancedConfig(newConfig);
    // Notify parent of config change
    if (selectedOperation) {
      onSelect(selectedOperation, objectDetails, newConfig);
    }
  };

  // Get categorized fields
  const { alwaysVisible, advancedSettings } = categorizeFields(
    advancedFieldDefinitions,
    selectedOperation
  );

  // Render a single config field based on its input_type
  const renderConfigField = (fieldDef) => {
    const value = advancedConfig[fieldDef.name] ?? fieldDef.default ?? '';

    switch (fieldDef.input_type) {
      case 'toggle':
        return (
          <FormGroup check key={fieldDef.name} className="mb-3">
            <Input
              type="checkbox"
              id={`config-${fieldDef.name}`}
              checked={!!value}
              onChange={(e) => updateConfig(fieldDef.name, e.target.checked)}
            />
            <Label check for={`config-${fieldDef.name}`}>
              {fieldDef.label}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </Label>
            {fieldDef.placeholder && (
              <FormText color="muted">{fieldDef.placeholder}</FormText>
            )}
          </FormGroup>
        );

      case 'dropdown':
        return (
          <FormGroup key={fieldDef.name} className="mb-3">
            <Label for={`config-${fieldDef.name}`}>
              {fieldDef.label}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </Label>
            <Input
              type="select"
              id={`config-${fieldDef.name}`}
              value={value}
              onChange={(e) => updateConfig(fieldDef.name, e.target.value)}
            >
              {!fieldDef.required && <option value="">Select...</option>}
              {fieldDef.supported_values?.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Input>
            {fieldDef.placeholder && (
              <FormText color="muted">{fieldDef.placeholder}</FormText>
            )}
          </FormGroup>
        );

      case 'textarea':
        return (
          <FormGroup key={fieldDef.name} className="mb-3">
            <Label for={`config-${fieldDef.name}`}>
              {fieldDef.label}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </Label>
            <Input
              type="textarea"
              id={`config-${fieldDef.name}`}
              value={value}
              placeholder={fieldDef.placeholder}
              onChange={(e) => updateConfig(fieldDef.name, e.target.value)}
              rows={4}
            />
          </FormGroup>
        );

      case 'text':
      default:
        return (
          <FormGroup key={fieldDef.name} className="mb-3">
            <Label for={`config-${fieldDef.name}`}>
              {fieldDef.label}
              {fieldDef.required && <span className="text-danger"> *</span>}
            </Label>
            <Input
              type={fieldDef.type === 'integer' ? 'number' : 'text'}
              id={`config-${fieldDef.name}`}
              value={value}
              placeholder={fieldDef.placeholder}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                updateConfig(
                  fieldDef.name,
                  fieldDef.type === 'integer' ? (Number.isNaN(n) ? '' : n) : e.target.value
                );
              }}
            />
          </FormGroup>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="sync-refresh">
          <h3><strong>2.</strong> Select Sync Behavior</h3>
        </div>
      </CardHeader>
      <CardBody>
        <Form>
          <div className="button-select">
            {supportedOperations?.map((operation) => (
              <Button
                outline
                key={operation}
                color={selectedOperation === operation ? "primary" : "light"}
                className={`select-item ${selectedOperation === operation ? 'active' : ''}`}
                onClick={() => {
                  // Check if selecting a different operation while there are mapping changes
                  if (hasMappingChanges && selectedOperation !== operation) {
                    setConfirmModal({ open: true, pendingOperation: operation });
                    return;
                  }
                  setSelectedOperation(operation);
                  onSelect(operation, objectDetails, advancedConfig);
                }}
              >
                <i className={`bi ${selectedOperation === operation ? 'bi-check-circle' : 'bi-circle'}`} />
                <div className="select-item-content">
                  <strong>{OPERATION_LABELS[operation] || operation}</strong>
                  <div className="small">{OPERATION_DESCRIPTIONS[operation]}</div>
                </div>
              </Button>
            ))}
          </div>

          {/* Always visible configuration fields (Tier 3) */}
          {alwaysVisible.length > 0 && (
            <div className="advanced-config-section mt-4">
              <h4>Configuration</h4>
              {alwaysVisible.map(renderConfigField)}
            </div>
          )}

          {/* Advanced settings (Tier 2) - collapsible */}
          {advancedSettings.length > 0 && (
            <div className="advanced-config-section mt-4">
              <Button
                color="link"
                onClick={() => setAdvancedOpen(!advancedOpen)}
                className="advanced-toggle p-0 btn-link-census"
              >
                <i className={`bi ${advancedOpen ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} />
                Advanced Settings
                <Badge className="advanced-count">{advancedSettings.length}</Badge>
              </Button>
              <Collapse isOpen={advancedOpen}>
                <div className="advanced-config-fields mt-3">
                  {advancedSettings.map(renderConfigField)}
                </div>
              </Collapse>
            </div>
          )}
        </Form>

        <Modal centered backdrop isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false, pendingOperation: null })}>
          <ModalHeader toggle={() => setConfirmModal({ open: false, pendingOperation: null })}>
            <i className="bi bi-exclamation-triangle me-2" />
            Confirm Change
          </ModalHeader>
          <ModalBody>
            This change will cause a loss of field mapping changes. Do you want to continue?
          </ModalBody>
          <ModalFooter>
            <Button className="promote" onClick={() => setConfirmModal({ open: false, pendingOperation: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const operation = confirmModal.pendingOperation;
                setConfirmModal({ open: false, pendingOperation: null });
                setSelectedOperation(operation);
                onSelect(operation, objectDetails, advancedConfig);
              }}
            >
              Continue
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  )
}
